import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Gift } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SurveyQuestion } from "@/components/SurveyQuestion";
import { SURVEY_SECTIONS } from "@/constants/survey";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STORAGE_KEY = "ud-survey";

export default function SurveyPage() {
  const { homepage } = useSettings();
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/shows").then((r) => { const seen = new Set(); setShows(r.data.filter((s) => s.active !== false && s.kind !== "tiktok" && !seen.has(s.name) && seen.add(s.name)).sort((a, b) => a.name.replace(/^the\s+/i, "").localeCompare(b.name.replace(/^the\s+/i, "")))); }).catch(() => {}); window.scrollTo(0, 0); }, []);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const steps = useMemo(() => [...SURVEY_SECTIONS, { id: "email", title: "Enter the drawing" }], []);
  const section = steps[step];
  const onChange = (id, v) => { setAnswers((a) => ({ ...a, [id]: v })); setErrors((e) => ({ ...e, [id]: undefined })); };

  const validate = () => {
    const errs = {};
    (section.questions || []).forEach((q) => {
      const v = answers[q.id];
      if (q.required && (v === undefined || v === "" || (Array.isArray(v) && !v.length))) errs[q.id] = "Please answer this question.";
    });
    setErrors(errs);
    return !Object.keys(errs).length;
  };
  const next = () => { if (validate()) setStep((s) => s + 1); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/survey", { email, answers });
      localStorage.setItem(STORAGE_KEY, "done");
      setDone(true);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };

  const prize = homepage.survey_prize || "$100";
  return (
    <div className="min-h-screen" data-testid="survey-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        {done ? (
          <div className="card-surface rounded-3xl p-10 text-center reveal" data-testid="survey-done">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300"><Check size={30} /></span>
            <h1 className="mt-6 font-display text-3xl sm:text-4xl font-black tracking-tight">You're entered!</h1>
            <p className="mt-3 text-slate-400">Thanks for helping shape Underdog Podcasts. If your name is drawn for the {prize} prize we'll reach out at the email you provided — and that's the only time you'll hear from us.</p>
            <Button onClick={() => navigate("/")} className="mt-8 rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="survey-done-home">Back to the shows</Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 text-sky-300"><Gift size={18} /><p className="eyebrow">Listener survey · win {prize}</p></div>
            <h1 className="mt-2 font-display text-3xl sm:text-5xl font-black tracking-tight" data-testid="survey-title">Help us make better shows.</h1>
            <p className="mt-3 text-slate-400">About 5 minutes. Complete it and you're entered to win {prize}.</p>
            <div className="mt-8 flex items-center gap-2" data-testid="survey-progress">
              {steps.map((s, i) => <span key={s.id} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-sky-400" : "bg-white/10"}`} />)}
            </div>
            <p className="mt-2 text-xs text-slate-500">Step {step + 1} of {steps.length} · <span className="text-slate-300">{section.title}</span></p>

            <div className="mt-6 card-surface rounded-3xl p-6 sm:p-8" data-testid={`survey-step-${section.id}`}>
              {section.help && <p className="text-sm text-slate-400 mb-2">{section.help}</p>}
              {section.id === "email" ? (
                <form onSubmit={submit} data-testid="survey-email-form">
                  <h2 className="font-display text-xl font-extrabold">Last step — where should we reach you if you win?</h2>
                  <div className="mt-5"><Label htmlFor="survey-email">Email address *</Label><Input id="survey-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 bg-white/5 border-white/10" data-testid="survey-email-input" /></div>
                  <p className="mt-3 rounded-xl bg-white/[0.04] border border-white/10 p-3 text-xs text-slate-300" data-testid="survey-email-note"><strong className="text-white">You are not joining a mailing list.</strong> Your email is used only to contact you if you're chosen at random as the winner — that's the only time you'll ever hear from us.</p>
                  <div className="mt-6 flex items-center justify-between gap-3">
                    <Button type="button" variant="ghost" onClick={() => setStep((s) => s - 1)} data-testid="survey-back"><ArrowLeft size={15} className="mr-1" /> Back</Button>
                    <Button type="submit" disabled={busy} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300 px-6" data-testid="survey-submit">{busy ? "Submitting…" : "Submit & enter the drawing"}</Button>
                  </div>
                </form>
              ) : (
                <>
                  {section.questions.map((q) => <SurveyQuestion key={q.id} q={q} answers={answers} onChange={onChange} shows={shows} error={errors[q.id]} />)}
                  <div className="mt-6 flex items-center justify-between gap-3">
                    {step > 0 ? <Button type="button" variant="ghost" onClick={() => setStep((s) => s - 1)} data-testid="survey-back"><ArrowLeft size={15} className="mr-1" /> Back</Button> : <Link to="/" className="text-sm text-slate-400 hover:text-white" data-testid="survey-cancel">Maybe later</Link>}
                    <Button type="button" onClick={next} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300 px-6" data-testid="survey-next">Continue <ArrowRight size={15} className="ml-1" /></Button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
